import TopNav from '@/components/TopNav/TopNav';
import { ROUTE_DASHBOARD_BOOKING_PAGES } from '@/constants/routes';
import {
	Box,
	Button,
	Flex,
	Menu,
	MenuButton,
	MenuDivider,
	MenuItem,
	MenuList,
	Skeleton,
	Stack,
} from '@chakra-ui/react';
import { VscChevronDown } from 'react-icons/vsc';
import SectionAppearance from './components/SectionAppearance/SectionAppearance';
import SectionCalendars from './components/SectionCalendars/SectionCalendars';
import SectionGeneral from './components/SectionGeneral/SectionGeneral';
import SectionHosts from './components/SectionHosts/SectionHosts';
import SectionLimits from './components/SectionLimits/SectionLimits';
import SectionNotifications from './components/SectionNotifications/SectionNotifications';
import SectionPto from './components/SectionPto/SectionPto';
import SectionQuestions from './components/SectionQuestions/SectionQuestions';
import SectionResources from './components/SectionResources/SectionResources';
import SectionSchedule from './components/SectionSchedule/SectionSchedule';
import SectionShare from './components/SectionShare/SectionShare';
import useServiceForm from './useEventTypeForm';

interface IEventTypeFormSuccessProps {
	isPreviewEnabled: boolean;
	togglePreview: () => void;
}

export default function EventTypeFormSuccess(props: IEventTypeFormSuccessProps) {
	const { t, copyLink, deleteEventType, handleChange, duplicateEventType, eventType, queryEdit, getEventTypeQuery } =
		useServiceForm();

	return (
		<Box
			overflow="auto"
			display={{
				base: props.isPreviewEnabled ? 'none' : 'grid',
				md: 'grid',
			}}
			transition="width .3s"
			maxW={{ base: '100%', md: 'md', lg: 'lg', xl: 'lg' }}
			minW={{ base: '100%', md: 'md', lg: 'lg', xl: 'lg' }}
			gridTemplateRows="auto 1fr"
		>
			<TopNav
				backLink={ROUTE_DASHBOARD_BOOKING_PAGES}
				backText={t('Back')}
				isLoading={queryEdit.isLoading || getEventTypeQuery.isLoading}
				title={eventType?.name || ''}
				action={
					<Menu>
						<MenuButton as={Button} colorScheme="blue" variant="link" fontWeight="400" rightIcon={<VscChevronDown />}>
							{t('Actions')}
						</MenuButton>
						<MenuList>
							<MenuItem
								display={{ base: 'initial', md: 'none' }}
								disabled={props.isPreviewEnabled}
								onClick={props.togglePreview}
							>
								{t('Show preview')}
							</MenuItem>
							<MenuItem isDisabled={!eventType} onClick={copyLink}>
								{t('Copy link')}
							</MenuItem>
							<MenuItem isDisabled={!eventType} onClick={duplicateEventType}>
								{t('Duplicate booking page')}
							</MenuItem>
							<MenuDivider />
							<MenuItem isDisabled={!eventType} onClick={deleteEventType}>
								{t('Delete booking page')}
							</MenuItem>
						</MenuList>
					</Menu>
				}
			/>

			{eventType ? (
				<Flex flex="1" flexDir="column" h="100%" overflow="auto" px="4">
					<SectionGeneral service={eventType} onChange={handleChange} />
					<SectionSchedule service={eventType} onChange={handleChange} />
					<SectionPto service={eventType} onChange={handleChange} />
					<SectionCalendars isLoading={queryEdit.isLoading} service={eventType} onChange={handleChange} />
					<SectionQuestions service={eventType} onChange={handleChange} />
					<SectionNotifications service={eventType} onChange={handleChange} />
					<SectionLimits service={eventType} onChange={handleChange} />
					<SectionHosts service={eventType} onChange={handleChange} />
					<SectionShare service={eventType} onChange={handleChange} />
					<SectionResources service={eventType} onChange={handleChange} />
					<SectionAppearance service={eventType} onChange={handleChange} isLoading={queryEdit.isLoading} />
					<Box mt="auto" pb="8" pt="4">
						<Button onClick={copyLink} w="100%" colorScheme="primary">
							{t('Copy link')}
						</Button>
					</Box>
				</Flex>
			) : (
				<Stack padding="0 1.3rem" spacing="1px">
					<Skeleton height="60px" />
					<Skeleton height="60px" />
					<Skeleton height="60px" />
					<Skeleton height="60px" />
					<Skeleton height="60px" />
					<Skeleton height="60px" />
					<Skeleton height="60px" />
					<Skeleton height="60px" />
					<Skeleton height="60px" />
				</Stack>
			)}
		</Box>
	);
}
